import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Select, MenuItem, Typography, CircularProgress, Button,
  TextField, Grid, Pagination, Box, InputAdornment,
  Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText
} from "@mui/material";
import Search from '@mui/icons-material/Search';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ProofApproval = () => {
  const [company_id, setCompany_id] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [commentError, setCommentError] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState("");
  const [updating, setUpdating] = useState(false);
  const [statusUpdateMessage, setStatusUpdateMessage] = useState({
    show: false,
    message: "",
    isError: false
  });
  const rowsPerPage = 5;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCompany_id(payload.company_id);
    } catch (error) {
      alert("Invalid token. Please log in again.");
    }
  }, []);

  const fetchDocuments = useCallback(async (currentPage = 1, search = "") => {
      const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
    if (!company_id) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: rowsPerPage,
        ...(search && { search })
      });
      const response = await axios.get(`${API_URL}/api/approval/all?${params}`,{
        headers:{
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true
      });
      setDocuments(response.data.data);
      setTotalCount(response.data.total);
    } catch (error) {
      setStatusUpdateMessage({
        show: true,
        message: "Failed to fetch documents",
        isError: true
      });
      setTimeout(() => {
        setStatusUpdateMessage({ show: false, message: "", isError: false });
      }, 5000);
    } finally {
      setLoading(false);
    }
  }, [company_id]);

  useEffect(() => {
    if (!company_id) return;
    fetchDocuments(1, searchTerm);
  }, [company_id, fetchDocuments]);

  const handleViewDocument = (fileUrl) => {
    if (!fileUrl) return;
    const url = fileUrl.startsWith('http') ? fileUrl : `${API_URL}/uploads/investment-proofs/${fileUrl}`;
    const ext = fileUrl.split('.').pop().toLowerCase().split('?')[0];
    
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
      window.open(`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`, '_blank');
    } else {
      window.open(url, "_blank");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchDocuments(newPage, searchTerm);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(1);
    fetchDocuments(1, value);
  };

  const handleStatusClick = (doc, status) => {
    setCurrentDoc(doc);
    setStatusToUpdate(status);
    setReviewComment("");
    setCommentError(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentDoc(null);
    setReviewComment("");
    setCommentError(false);
  };

  const handleUpdateStatus = async () => {
    if (!currentDoc || !statusToUpdate || !company_id) return;
    
    // Validate comment for Rejected status
    if (statusToUpdate === "Rejected" && (!reviewComment || reviewComment.trim() === "")) {
      setCommentError(true);
      return;
    }
    
    setStatusUpdateMessage({ show: false, message: "", isError: false });
    
    try {
      const token = localStorage.getItem('token');
      setUpdating(true);
      const response = await axios.put(`${API_URL}/api/approval/update-status`, {
        employee_id: currentDoc.employee_id,
        category: currentDoc.category,
        status: statusToUpdate,
        review_comment: reviewComment,
      },{
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true
        
      });
      
      if (response.data.success) {
        // Refetch the current page to update the list
        fetchDocuments(page, searchTerm);
        
        handleCloseDialog();
        
        setStatusUpdateMessage({
          show: true,
          message: `Status successfully updated to ${statusToUpdate}`,
          isError: false
        });
        
        setTimeout(() => {
          setStatusUpdateMessage({ show: false, message: "", isError: false });
        }, 5000);
      } else {
        setStatusUpdateMessage({
          show: true,
          message: response.data.message || "Update failed",
          isError: true
        });
      }
    } catch (error) {
      setStatusUpdateMessage({
        show: true,
        message: "Failed to update status",
        isError: true
      });
    } finally {
      setUpdating(false);
    }
  };

  if (!company_id) {
    return <CircularProgress />;
  }

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const paginatedDocuments = documents; // Now documents is already paginated from backend

  return (
    <div style={{ maxWidth: "98%", marginTop: "40px", marginLeft: "15px" }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        View Document and Approval
      </Typography>

      {statusUpdateMessage.show && (
        <Box 
          sx={{ 
            py: 1, 
            px: 2, 
            mb: 2, 
            borderRadius: 1,
            backgroundColor: statusUpdateMessage.isError ? '#FEE2E2' : '#ECFDF5',
            color: statusUpdateMessage.isError ? '#991B1B' : '#065F46',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Typography>{statusUpdateMessage.message}</Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: <InputAdornment position="end"><Search color="action" /></InputAdornment>,
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Employee ID</strong></TableCell>
                  <TableCell><strong>FirstName</strong></TableCell>
                  <TableCell><strong>LastName</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Supporting Document</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Reviewer Comment</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDocuments.length > 0 ? (
                  paginatedDocuments.map((doc) => (
                    <TableRow key={`${doc.employee_id}-${doc.category}`}>
                      <TableCell>{doc.employee_id}</TableCell>
                      <TableCell>{doc.personal?.firstName || "N/A"}</TableCell>
                      <TableCell>{doc.personal?.lastName || "N/A"}</TableCell>
                      <TableCell>{doc.category}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleViewDocument(doc.proof_file_path);
                          }}
                        >
                          View
                        </Button>
                      </TableCell>                    
                      <TableCell>
                        <Select
                          value={doc.status || "Pending"}
                          onChange={(e) => handleStatusClick(doc, e.target.value)}
                          variant="outlined"
                          size="small"
                        >
                          <MenuItem value="Pending">Pending</MenuItem>
                          <MenuItem value="Approved">Approved</MenuItem>
                          <MenuItem value="Rejected">Rejected</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {doc.review_comment || "No comments"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No documents available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}

      {/* Comment Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Update Status to {statusToUpdate}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={statusToUpdate === "Rejected" ? "Reason for Rejection (Required)" : "Reviewer Comment"}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={reviewComment}
            onChange={(e) => {
              setReviewComment(e.target.value);
              if(statusToUpdate === "Rejected") {
                setCommentError(e.target.value.trim() === "");
              }
            }}
            placeholder={statusToUpdate === "Rejected" 
              ? "Please provide a reason for rejection (required)" 
              : "Please provide your comments for this decision (optional)"}
            error={commentError}
            required={statusToUpdate === "Rejected"}
          />
          {commentError && (
            <FormHelperText error>
              Comment is required when rejecting a document
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateStatus} 
            color="primary" 
            variant="contained" 
            disabled={updating || (statusToUpdate === "Rejected" && (!reviewComment || reviewComment.trim() === ""))}
          >
            {updating ? "Updating..." : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProofApproval;